/*
 * File: FlyToTargetContext.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on June 1, 2022 at 7:59 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 4, 2025 at 6:35 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	useState,
	useRef,
	useEffect,
	cloneElement,
	useContext,
	forwardRef,
	useMemo
} from 'react';
import PropTypes from 'prop-types';
import { useDebouncedCallback, useWindowResize } from 'beautiful-react-hooks';

// Context
import AnimationContextBuilder from '../../context/AnimationContext';

// Hooks
import useMediaQuery from '../../hooks/useMediaQuery';

// Constants
import { tabBarBreakpoint } from '../../styles/constants';

// Styles
import * as S from './FlyToTargetContext.styles';


/**
 * Constants
 */

const FLIGHT_RISK_SIZE = {
	width: 42,
	height: 42
};


/**
 * Component
 */

export const FlyToTargetContext = forwardRef(({
	children, targets, flyElement, navigationState, refOffsetTop, setAnimationDuration
}, ref) => {

	// Get context from navigation parent
	let animationState = navigationState;
	const contextResult = useContext(AnimationContextBuilder);
	if (!animationState && contextResult) animationState = contextResult.state;

	// Create state handlers
	const [shouldMove, setShouldMove] = useState(false);
	const [contextLeftPosition, setContextLeftPosition] = useState(0);
	const [contextTopPosition, setContextTopPosition] = useState(0);

	// Check if mobile screen size
	const isMobile = useMediaQuery(`(max-width: ${tabBarBreakpoint}em)`);

	// Get target position parameters
	const { left: targetLeft, top: targetTop } = (isMobile ? animationState[targets.mobile] : animationState[targets.desktop]) || { left: 0, top: 0 };

	// Generate transformation x coordinate
	const transformX = useMemo(() => targetLeft - contextLeftPosition, [targetLeft, contextLeftPosition]);

	// Generate animation duration
	const transformY = useMemo(() => targetTop - contextTopPosition, [targetTop, contextTopPosition]);

	// Generate animation duration
	const animationDuration = useMemo(() => {
		const hypotenuse = Math.sqrt(transformX ** 2 + transformY ** 2);
		return hypotenuse / 1000;
	}, [transformX, transformY]);

	// Create reference for components
	const isMounted = useRef(true);

	// Handle animate fly action
	const flyToTarget = () => {

		// Update animation state
		setShouldMove(true);

		// Reset animation state
		setTimeout(() => {
			setShouldMove(false);
		}, animationDuration * 1000);
	};

	// Handle debounce function for updating positions
	const debounceUpdatePosition = useDebouncedCallback(() => {
		if (isMounted.current) {

			// Set new position values
			setContextLeftPosition(ref?.current?.getBoundingClientRect()?.left);
			setContextTopPosition(ref?.current?.getBoundingClientRect()?.top || 0 + refOffsetTop);
		}
	}, [], 300);

	// Handle actions on animation duration update
	useEffect(() => {

		// Set animation duration
		setAnimationDuration(animationDuration);

	}, [setAnimationDuration, animationDuration]);

	// Handle actions on component load
	useEffect(() => {

		// Initialize temporary state
		isMounted.current = true;

		// Set new position values
		debounceUpdatePosition();

		// Attach scroll listener
		if (isMounted.current) {
			document.addEventListener('scroll', () => {
				if (isMounted.current) {

					// Update positions with debounce
					debounceUpdatePosition();
				}
			}, true);
		}

		// Handle actions on dismount
		return () => { isMounted.current = false; };

	}, []);

	// Handle actions on window resize
	useWindowResize(() => {

		// Set new position values
		debounceUpdatePosition();
	});

	// Render component
	return (
		<S.Wrapper>
			<S.FlightRisk
				className={shouldMove ? 'move' : null}
				onClick={flyToTarget}
				style={{
					top: contextTopPosition,
					left: contextLeftPosition,
				}}
				$size={FLIGHT_RISK_SIZE}
				$transformX={transformX}
				$transformY={transformY}
				$animationDuration={animationDuration}
			>
				{flyElement}
			</S.FlightRisk>
			{Array.isArray(children)
				? children.map((child) => cloneElement(child, { fireAnimation: flyToTarget }))
				: cloneElement(children, { fireAnimation: flyToTarget })}
		</S.Wrapper>
	);
});


/**
 * Configuration
 */

FlyToTargetContext.displayName = 'FlyToTargetContext';
FlyToTargetContext.propTypes = {
	children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
	targets: PropTypes.shape({
		desktop: PropTypes.string,
		mobile: PropTypes.string
	}),
	flyElement: PropTypes.node,
	navigationState: PropTypes.shape(),
	refOffsetTop: PropTypes.number,
	setAnimationDuration: PropTypes.func
};
FlyToTargetContext.defaultProps = {
	children: null,
	targets: {},
	flyElement: null,
	navigationState: null,
	refOffsetTop: 0,
	setAnimationDuration: null
};
